<template>
  <span
    v-if="rawTotalRoles < 1"
    class="attached-to-role-empty"
    @click.stop="$emit('click')"
  >—</span>
  <div
    v-else
    class="attached-to-role-badge-container"
    :class="{ 'attached-to-role-badge-container--hovered': isActiveVisual }"
    @click.stop="$emit('click')"
  >
    <div
      class="attached-to-role-badge-wrap"
    >
      <div
        class="attached-to-role-badge"
        :class="{ 'attached-to-role-badge--hovered': isActiveVisual }"
      >
        <span class="attached-to-role-badge__primary">{{ roleName }}</span>
      </div>
    </div>
    <div
      v-if="showMoreBadge"
      class="attached-to-role-badge__more"
      :class="{ 'attached-to-role-badge__more--hovered': isActiveVisual }"
      >+{{ additionalRolesCount }}</div
    >
  </div>
</template>

<script setup>
import { computed } from "vue";

defineEmits(["click"]);

const props = defineProps({
  /** Label for the first / primary role (e.g. "Budget Owner"). */
  roleName: {
    type: String,
    default: "",
  },
  /**
   * Total number of roles attached. Additional count shown is totalRoles − 1.
   * Example: 5 roles → primary label + "+4".
   */
  totalRoles: {
    type: Number,
    default: 1,
  },
  /** Selected / highlighted state (persistent purple treatment). */
  highlighted: {
    type: Boolean,
    default: false,
  },
  /**
   * Hovered / active visuals (purple treatment). When false, pointer hover does not change styling (same pattern as KPINameCard).
   */
  hovered: {
    type: Boolean,
    default: false,
  },
});

const rawTotalRoles = computed(() => Number(props.totalRoles) || 0);
const total = computed(() =>
  rawTotalRoles.value < 1 ? 0 : Math.max(1, rawTotalRoles.value)
);
const additionalRolesCount = computed(() => Math.max(0, total.value - 1));
const showMoreBadge = computed(() => additionalRolesCount.value > 0);
/** Persistent highlight or Storybook / forced hover visuals */
const isActiveVisual = computed(() => props.hovered || props.highlighted);
</script>

<style scoped>
.attached-to-role-badge {
  display: flex;
  text-align: center;
  align-items: center;
  box-sizing: border-box;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  width: fit-content !important;
  cursor: pointer;
  white-space: nowrap;
  max-width: 100px;
}

.attached-to-role-badge-container {
  box-sizing: border-box;
  padding: 2px 4px;
  border-radius: 5px;
  background: #fff;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  overflow: hidden;
}

.attached-to-role-badge-wrap {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.attached-to-role-badge-container--hovered {
  background: #f4ebff;
}

.attached-to-role-badge--hovered {
  border-color: #d6bbfb;
  background: #faf5ff;
}

.attached-to-role-badge__more--hovered {
  border-color: #d6bbfb;
  background: #f4ebff;
}

.attached-to-role-badge__primary {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attached-to-role-empty {
  color: #98a2b3;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.attached-to-role-badge__more {
  display: inline-flex;
  flex-shrink: 0;
  align-self: center;
  padding: 2px 6px;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid #eaecf0;
  background: #f9fafb;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}
</style>
